import type { ListItemTextProps } from '@mui/material/ListItemText';
import type { TypographyProps } from '@mui/material/Typography';
import Box from '@mui/material/Box';
import MuiListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';

import { useHasContent } from 'common/hooks/useHasContent';

interface IProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    secondaryTypographyProps?: TypographyProps<any>;
    className?: string;
    children?: React.ReactNode;
    heading?: React.ReactNode;
    showFullSecondary?: boolean;
    // Wrap title over multiple lines
    wrapTitle?: boolean;
    sx?: SxProps<Theme>;
}

export const ListItemText = ({
    className,
    children,
    heading,
    primary,
    secondary,
    showFullSecondary,
    wrapTitle,
    sx,
    ...other
}: ListItemTextProps & IProps) => {
    const { hasContent, contentRef } = useHasContent(children);

    return (
        <Box
            className={className}
            sx={[
                (theme) => ({
                    overflow: 'hidden',
                    flex: 1,
                    '& > :not(:last-child)': {
                        [theme.breakpoints.up('sm')]: {
                            marginRight: theme.spacing(3),
                        },
                    },
                    ...(!showFullSecondary && {
                        '& p': {
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            [theme.breakpoints.up('sm')]: {
                                whiteSpace: 'normal',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                            },
                        },
                        [theme.breakpoints.up('sm')]: {
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                        },
                    }),
                }),
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            <MuiListItemText disableTypography {...other}>
                {heading && (
                    <Typography
                        sx={{
                            color: 'primary.main',
                            fontSize: '1.4rem',
                            fontWeight: 700,
                            lineHeight: '1.2',
                        }}
                    >
                        {heading}
                    </Typography>
                )}

                {primary && (
                    <Typography
                        lineHeight={1.1}
                        noWrap={!wrapTitle}
                        variant="h4"
                    >
                        {primary}
                    </Typography>
                )}
                {secondary && (
                    <Typography color="text.secondary" variant="body2">
                        {secondary}
                    </Typography>
                )}

                {children && (
                    <Box
                        ref={contentRef}
                        sx={{
                            mt: (primary || secondary) && hasContent ? 1 : 0,
                        }}
                    >
                        {children}
                    </Box>
                )}
            </MuiListItemText>
        </Box>
    );
};
